import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FiSmartphone } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useBiometricAuth } from '../hooks/useBiometricAuth';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import Card from '../components/common/Card';
import Spinner from '../components/common/Spinner';
import { toast } from '../utils/toast';

// Storage key for remembering last email
const LAST_EMAIL_KEY = 'last_login_email';

const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();
  const {
    isEnrolled,
    isLoading: biometricLoading,
    isAuthenticating,
    biometricType,
    authenticate,
  } = useBiometricAuth();

  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [showBiometricOption, setShowBiometricOption] = useState(false);

  // Check if user has enrolled biometrics and has a saved email
  useEffect(() => {
    const savedEmail = localStorage.getItem(LAST_EMAIL_KEY);
    if (savedEmail && isEnrolled) {
      setShowBiometricOption(true);
      setFormData((prev) => ({ ...prev, email: savedEmail }));
    }
  }, [isEnrolled]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setIsLoading(true);
    try {
      await login(formData);

      // Save email for biometric login next time
      localStorage.setItem(LAST_EMAIL_KEY, formData.email);

      toast.success('Login successful!');

      // Check if there's a redirect URL stored from previous 401
      const redirectUrl = sessionStorage.getItem('redirectAfterLogin');
      if (redirectUrl) {
        sessionStorage.removeItem('redirectAfterLogin');
        navigate(redirectUrl);
      } else {
        navigate('/');
      }
    } catch (error: any) {
      // Surface the actual cause when known. The bare default ("Login
      // failed. Please check your credentials.") was misleading on
      // rate-limit / network / 5xx — users would re-try the same
      // credentials when the issue wasn't credentials at all.
      const status = error?.status || error?.response?.status;
      const serverMsg = error?.response?.data?.error?.message
        || error?.response?.data?.message
        || error?.message;
      let message: string;
      if (status === 429) {
        message = 'Too many login attempts. Please wait a minute and try again.';
      } else if (status === 401) {
        message = 'Invalid email or password.';
      } else if (status >= 500) {
        message = 'Server error. Please try again in a moment.';
      } else if (!status && serverMsg?.toLowerCase().includes('failed to fetch')) {
        message = 'Cannot reach the server. Check your network and try again.';
      } else {
        message = serverMsg || 'Login failed. Please check your credentials.';
      }
      console.warn('[Login] Submit failed', { status, serverMsg, error });
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle biometric authentication
   * This verifies the user's identity locally, then prompts for password
   * for security (since we don't have passkey backend support yet)
   */
  const handleBiometricLogin = async () => {
    const result = await authenticate();

    if (result.success) {
      toast.success(`${biometricType} verified! Please enter your password.`);
      // Focus password field after successful biometric verification
      const passwordInput = document.querySelector<HTMLInputElement>('input[type="password"]');
      if (passwordInput) {
        passwordInput.focus();
      }
    } else {
      toast.error(result.error || 'Biometric authentication failed');
    }
  };

  // Loading state while checking biometric availability
  if (biometricLoading) {
    return (
      <Card>
        <div className="flex items-center justify-center py-8">
          <Spinner size="lg" />
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Sign In</h2>
          <p className="text-gray-600 mt-2">Welcome back! Please sign in to continue.</p>
        </div>

        {/* Biometric Login Option */}
        {showBiometricOption && (
          <div className="space-y-3">
            <button
              type="button"
              onClick={handleBiometricLogin}
              disabled={isAuthenticating}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isAuthenticating ? (
                <Spinner size="sm" />
              ) : (
                <FiSmartphone className="w-5 h-5 text-gray-600" />
              )}
              <span className="font-medium text-gray-700">
                {isAuthenticating ? 'Verifying...' : `Sign in with ${biometricType}`}
              </span>
            </button>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">or continue with email</span>
              </div>
            </div>
          </div>
        )}

        <Input
          type="email"
          label="Email Address"
          placeholder="you@example.com"
          value={formData.email}
          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          error={errors.email}
          required
        />

        <Input
          type="password"
          label="Password"
          placeholder="••••••••"
          value={formData.password}
          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
          error={errors.password}
          required
        />

        <Button type="submit" fullWidth isLoading={isLoading}>
          Sign In
        </Button>

        <div className="text-center space-y-2">
          <p className="text-sm text-gray-600">
            Don't have an account?{' '}
            <Link to="/register" className="text-blue-600 hover:text-blue-700 font-medium">
              Sign up
            </Link>
          </p>

          {/* Tip about biometric authentication */}
          {!showBiometricOption && !biometricLoading && (
            <p className="text-xs text-gray-500">
              Enable {biometricType || 'biometric login'} in Settings after signing in
            </p>
          )}
        </div>
      </form>
    </Card>
  );
};

export default Login;
